import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/feature/EmptyState";
import { reviews, reviewCategories } from "@/mocks/reviews";

const sortOptions = [
  { key: "time", label: "按时间" },
  { key: "rating", label: "按评分" },
];

const PAGE_SIZE = 6;

export default function Reviews() {
  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState("time");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const list = reviews.filter((r) => category === "全部" || r.category === category);
    return [...list].sort((a, b) =>
      sort === "rating" ? b.rating - a.rating : b.date.localeCompare(a.date)
    );
  }, [category, sort]);

  const shown = filtered.slice(0, visible);

  const changeCategory = (c: string) => {
    setCategory(c);
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-xs text-secondary-500 mb-3">
            <Link to="/" className="hover:text-accent-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">影评与专题</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">影评与专题</h1>
          <p className="mt-3 text-sm md:text-base text-secondary-500 max-w-2xl">
            汇集影迷与影评人的深度解读，从表演、镜头到主题，多角度读懂这部经典之作。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {reviewCategories.map((c) => (
              <button
                key={c}
                onClick={() => changeCategory(c)}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  category === c
                    ? "bg-primary-500 text-foreground-950"
                    : "bg-background-100 text-foreground-700 border border-background-200 hover:border-accent-500"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-secondary-500 whitespace-nowrap">排序</span>
            <div className="flex p-1 rounded-full bg-background-100 border border-background-200">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSort(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    sort === opt.key ? "bg-accent-500 text-primary-950" : "text-secondary-500 hover:text-foreground-900"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {shown.map((review) => (
                <Link
                  key={review.id}
                  to={`/reviews/detail/${review.id}`}
                  className="group flex flex-col rounded-lg overflow-hidden bg-background-100 border border-background-200 hover:border-primary-500 transition-colors"
                >
                  <div className="relative w-full aspect-video overflow-hidden bg-background-200">
                    <img
                      src={review.cover}
                      alt={review.title}
                      title={`${review.title} - 色戒影评`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-background-50/85 text-xs text-foreground-900 backdrop-blur-sm">
                      {review.category}
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col p-4 md:p-5">
                    <div className="flex items-center gap-2 text-xs text-secondary-500">
                      <span className="inline-flex items-center gap-1 text-accent-500 font-medium">
                        <i className="ri-star-fill text-[11px]" />
                        {review.rating.toFixed(1)}
                      </span>
                      <span className="w-px h-3 bg-background-300" />
                      <span className="truncate">{review.author}</span>
                    </div>
                    <h2 className="mt-2.5 font-heading text-base md:text-lg font-semibold text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-2">
                      {review.title}
                    </h2>
                    <p className="mt-2 text-xs md:text-sm text-secondary-500 line-clamp-3 leading-relaxed">{review.excerpt}</p>
                    <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between text-xs text-secondary-500">
                      <span>{review.date}</span>
                      <span className="inline-flex items-center gap-1 group-hover:text-accent-500 transition-colors">
                        阅读全文
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {visible < filtered.length && (
              <div className="mt-10 flex justify-center">
                <button
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-100 border border-background-200 text-foreground-800 text-sm hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  加载更多
                </button>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon="ri-file-text-line"
            title="暂无影评，敬请期待"
            description="该分类下还没有内容，换个分类看看吧。"
          />
        )}
      </div>
    </div>
  );
}